// =============================================================================
// EXPENSE POLICY RULES
// Extends rulesEngine.ts for expense-specific policy enforcement
// Uses work_item_settings.expense_policy_rules (JSONB) for storage
// =============================================================================

import type { RuleDef, RuleResult, RulesEvaluationResult } from '@/components/forms/engine/rulesEngine';

// ============================================================================
// TYPES
// ============================================================================

export type ExpensePolicyActionType =
  | 'block_submission'
  | 'require_approval'
  | 'flag_for_review'
  | 'auto_categorize'
  | 'warn_user'
  | 'require_vendor_registration'
  | 'auto_match_supplier'
  | 'auto_approve'
  | 'auto_assign_supplier';

export type ExpensePolicyRuleType =
  | 'spending_limit'
  | 'category_restriction'
  | 'approval_threshold'
  | 'vendor_policy'
  | 'payment_method_policy'
  | 'daily_limit'
  | 'weekly_limit'
  | 'monthly_limit'
  | 'receipt_amount_threshold'
  | 'duplicate_detection'
  | 'recurring_expense'
  | 'missing_receipt'
  | 'supplier_match_policy'
  | 'per_diem_policy';

export interface ExpensePolicyRule {
  id: string;
  client_id: number;
  rule_name: string;
  rule_type: ExpensePolicyRuleType;
  priority: number;
  is_active: boolean;
  conditions: {
    all?: ExpensePolicyCondition[];
    any?: ExpensePolicyCondition[];
  };
  action_type: ExpensePolicyActionType;
  action_config: {
    approval_level?: number;
    category_id?: number | null;
    category_name?: string | null;
    message?: string;
    severity?: 'info' | 'warning' | 'error';
    auto_create_vendor?: boolean;
    threshold_amount?: number;
    lookback_days?: number;
    // Auto-approval config
    max_amount?: number;
    whitelisted_supplier_domains?: string[];
    whitelisted_supplier_ids?: string[];
    ai_confidence_threshold?: number;
    recurring_only?: boolean;
    require_receipt?: boolean;
    // Auto-assign supplier config (auto_assign_supplier action)
    contact_id?: string;
    contact_name?: string;
    account_code?: string;
  };
  target_user_ids?: number[];
  target_roles?: string[];
  applies_to_all?: boolean;
  supplier_rules?: Array<{
    id?: string;
    rule_name?: string;
    // Supplier match-rule fields (vendor pattern → assign contact)
    match_field?: string;
    operator?: string;
    match_value?: string;
    contact_id?: string | number;
    contact_name?: string;
    case_sensitive?: boolean;
    auto_categorize?: boolean;
    account_code?: string;
    priority?: number;
    // Per-supplier intake-config fields (supplier-rules API shape)
    default_workspace?: string | null;
    default_gl_account_code?: string | null;
    default_tax_rate_id?: number | null;
    auto_approve_below?: number | null;
    require_po_above?: number | null;
    default_payment_terms_days?: number | null;
    preferred_payment_method?: string | null;
    notify_emails?: string[] | null;
    /** Legacy field — kept for backward compatibility */
    assigned_supplier_id?: string;
  }>;
  times_applied: number;
  last_applied_at?: string;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface ExpensePolicyCondition {
  field: 'amount' | 'currency' | 'category' | 'category_id' | 'vendor' | 'vendor_id' | 'supplier_id' | 'supplier_vat' | 'supplier_email_domain' | 'payment_method' | 'expense_date' | 'user_id' | 'user_role' | 'expense_type' | 'expense_subtype' | 'receipt_amount' | 'has_receipt' | 'receipt_number' | 'per_diem_days' | 'per_diem_amount' | 'per_diem_location' | 'per_diem_trip_duration' | 'line_gl_account' | 'line_allocation_type' | 'line_quantity' | 'line_is_billable' | 'line_total';
  operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'in' | 'not_in' | 'between';
  value: string | number | (string | number)[];
  case_sensitive?: boolean;
}

export interface ExpensePolicyMatch {
  rule: ExpensePolicyRule;
  matched_conditions: number;
  matched_condition_values?: ExpensePolicyCondition[];
  action: ExpensePolicyActionType;
  action_config: ExpensePolicyRule['action_config'];
}

export interface ExpensePolicyEvaluationResult extends RulesEvaluationResult {
  /** All policy rules that matched */
  matchedPolicies: ExpensePolicyMatch[];
  /** Whether any block_submission rule fired */
  hasBlockers: boolean;
  /** Whether any require_approval rule fired */
  requiresApproval: boolean;
  /** Required approval level (highest from all matching rules) */
  requiredApprovalLevel: number;
  /** All blocker messages */
  blockerMessages: string[];
  /** All warning messages */
  warningMessages: string[];
  /** Flagged fields with messages */
  flaggedFields: Map<string, { message: string; severity: string }>;
  /** Auto-categorization suggestions */
  autoCategorize: { category_id?: number; category_name?: string } | null;
  /** Vendor registration requirements */
  requireVendorRegistration: { vendorName: string; autoCreate: boolean } | null;
  /** Auto-approval decision (if any auto_approve rule matched) */
  autoApproval: {
    ruleId: string;
    ruleName: string;
    reason: string;
    maxAmount?: number;
    aiConfidenceThreshold?: number;
  } | null;
  /** Sync fields from rulesEngine */
  hasBlockersFromRules: boolean;
  blockerMessagesFromRules: string[];
  warningMessagesFromRules: string[];
  flaggedFieldsFromRules: Map<string, { message: string; severity: string }>;
}

// ============================================================================
// EXPENSE-SPECIFIC CONDITION EVALUATION
// ============================================================================

export interface ExpenseLineItemData {
  expense_account_code?: string;
  quantity?: number;
  allocation_type?: string;
  is_billable?: boolean;
  line_total?: number;
}

export interface ExpenseData {
  amount: number;
  /** ISO-4217 currency the amount/receipt_amount is denominated in. */
  currency?: string;
  category?: string | number;
  category_id?: number;
  vendor?: string | number;
  vendor_id?: number;
  payment_method?: string;
  expense_date?: string;
  user_id?: number;
  user_role?: string;
  expense_type?: string;
  expense_subtype?: string;
  receipt_amount?: number;
  has_receipt?: boolean;
  receipt_number?: string;
  per_diem_days?: number;
  per_diem_amount?: number;
  per_diem_location?: string;
  per_diem_trip_duration?: number;
  line_items?: ExpenseLineItemData[];
  [key: string]: unknown;
}

function getExpenseFieldValue(data: ExpenseData, field: string): unknown {
  // Line-item-level fields: return array of values from all line items
  if (field.startsWith('line_')) {
    const items = Array.isArray(data.line_items) ? data.line_items : [];
    const lineField = field.replace('line_', '');
    return items.map(li => (li as Record<string, unknown>)[lineField]);
  }
  switch (field) {
    case 'amount':
      return Math.abs(Number(data.amount || 0));
    case 'currency':
      return data.currency;
    case 'category':
      return data.category;
    case 'category_id':
      return data.category_id;
    case 'vendor':
      return data.vendor;
    case 'vendor_id':
      return data.vendor_id;
    case 'payment_method':
      return data.payment_method;
    case 'expense_date':
      return data.expense_date;
    case 'user_id':
      return data.user_id;
    case 'user_role':
      return data.user_role;
    case 'expense_type':
      return data.expense_type;
    case 'expense_subtype':
      return data.expense_subtype;
    case 'receipt_amount':
      return data.receipt_amount;
    case 'has_receipt':
      return data.has_receipt;
    case 'receipt_number':
      return data.receipt_number;
    case 'per_diem_days':
      return data.per_diem_days;
    case 'per_diem_amount':
      return data.per_diem_amount;
    case 'per_diem_location':
      return data.per_diem_location;
    case 'per_diem_trip_duration':
      return data.per_diem_trip_duration;
    default:
      return data[field];
  }
}

function evaluateExpenseCondition(
  data: ExpenseData,
  cond: ExpensePolicyCondition
): boolean {
  const raw = getExpenseFieldValue(data, cond.field);

  // expense_date compares as timestamps — Number('2026-01-05') is NaN and would
  // make >/>=/</<=/between silently never match.
  const asComparableNumber = (v: unknown): number =>
    cond.field === 'expense_date' ? Date.parse(String(v ?? '')) : Number(v);

  // Line-item-level fields return an array of values — condition matches if ANY line item satisfies it
  if (cond.field.startsWith('line_') && Array.isArray(raw)) {
    if (raw.length === 0) return false;
    return raw.some((lineValue) => {
      const cs = cond.case_sensitive === true;
      const asString = (v: unknown) => (v == null ? '' : String(v));
      const haystack = cs ? asString(lineValue) : asString(lineValue).toLowerCase();
      const needleStr = cs ? asString(cond.value) : asString(cond.value).toLowerCase();

      switch (cond.operator) {
        case 'contains':
          return haystack.includes(needleStr);
        case '=':
          return haystack === needleStr;
        case '!=':
          return haystack !== needleStr;
        case 'in': {
          const arr = Array.isArray(cond.value) ? cond.value : [cond.value];
          return arr.map((v) => (cs ? asString(v) : asString(v).toLowerCase())).includes(haystack);
        }
        case 'not_in': {
          const arr = Array.isArray(cond.value) ? cond.value : [cond.value];
          return !arr.map((v) => (cs ? asString(v) : asString(v).toLowerCase())).includes(haystack);
        }
        case '>':
          return asComparableNumber(lineValue) > asComparableNumber(cond.value);
        case '>=':
          return asComparableNumber(lineValue) >= asComparableNumber(cond.value);
        case '<':
          return asComparableNumber(lineValue) < asComparableNumber(cond.value);
        case '<=':
          return asComparableNumber(lineValue) <= asComparableNumber(cond.value);
        case 'between': {
          if (!Array.isArray(cond.value) || cond.value.length !== 2) return false;
          const n = asComparableNumber(lineValue);
          return n >= asComparableNumber(cond.value[0]) && n <= asComparableNumber(cond.value[1]);
        }
        default:
          return false;
      }
    });
  }

  const cs = cond.case_sensitive === true;

  const asString = (v: unknown) => (v == null ? '' : String(v));
  const haystack = cs ? asString(raw) : asString(raw).toLowerCase();
  const needleStr = cs ? asString(cond.value) : asString(cond.value).toLowerCase();

  switch (cond.operator) {
    case 'contains':
      return haystack.includes(needleStr);
    case '=':
      return haystack === needleStr;
    case '!=':
      return haystack !== needleStr;
    case 'in': {
      const arr = Array.isArray(cond.value) ? cond.value : [cond.value];
      return arr.map((v) => (cs ? asString(v) : asString(v).toLowerCase())).includes(haystack);
    }
    case 'not_in': {
      const arr = Array.isArray(cond.value) ? cond.value : [cond.value];
      return !arr.map((v) => (cs ? asString(v) : asString(v).toLowerCase())).includes(haystack);
    }
    case '>':
      return asComparableNumber(raw) > asComparableNumber(cond.value);
    case '>=':
      return asComparableNumber(raw) >= asComparableNumber(cond.value);
    case '<':
      return asComparableNumber(raw) < asComparableNumber(cond.value);
    case '<=':
      return asComparableNumber(raw) <= asComparableNumber(cond.value);
    case 'between': {
      if (!Array.isArray(cond.value) || cond.value.length !== 2) return false;
      const n = asComparableNumber(raw);
      return n >= asComparableNumber(cond.value[0]) && n <= asComparableNumber(cond.value[1]);
    }
    default:
      return false;
  }
}

// ============================================================================
// POLICY RULE MATCHING
// ============================================================================

export function expensePolicyMatches(
  rule: ExpensePolicyRule,
  data: ExpenseData
): ExpensePolicyMatch | null {
  if (!rule.is_active) return null;

  const conds = rule.conditions || {};
  const all = Array.isArray(conds.all) ? conds.all : [];
  const any = Array.isArray(conds.any) ? conds.any : [];

  if (all.length === 0 && any.length === 0) return null;

  // All conditions must match
  const allMet = all.length === 0 || all.every((c) => evaluateExpenseCondition(data, c));
  if (!allMet) return null;

  // At least one "any" condition must match
  let matchedAny = 0;
  const matchedConditions: ExpensePolicyCondition[] = [...all];
  if (any.length > 0) {
    const anyMatches = any.filter((c) => evaluateExpenseCondition(data, c));
    matchedAny = anyMatches.length;
    if (matchedAny === 0) return null;
    matchedConditions.push(...anyMatches);
  }

  return {
    rule,
    matched_conditions: all.length + matchedAny,
    matched_condition_values: matchedConditions,
    action: rule.action_type,
    action_config: rule.action_config,
  };
}

/** Find all matching expense policy rules (highest priority first). */
export function findApplicableExpensePolicies(
  rules: ExpensePolicyRule[],
  data: ExpenseData
): ExpensePolicyMatch[] {
  const sorted = [...rules].sort((a, b) => b.priority - a.priority);
  const matches: ExpensePolicyMatch[] = [];

  for (const rule of sorted) {
    const match = expensePolicyMatches(rule, data);
    if (match) {
      matches.push(match);
    }
  }

  return matches;
}

// ============================================================================
// POLICY EVALUATION RESULT AGGREGATION
// =============================================================================

export function aggregateExpensePolicyResults(
  matches: ExpensePolicyMatch[]
): ExpensePolicyEvaluationResult {
  const result: ExpensePolicyEvaluationResult = {
    matchedPolicies: matches,
    hasBlockers: false,
    requiresApproval: false,
    requiredApprovalLevel: 0,
    blockerMessages: [],
    warningMessages: [],
    flaggedFields: new Map(),
    autoCategorize: null,
    requireVendorRegistration: null,
    autoApproval: null,
    firedRules: [],
    hasBlockersFromRules: false,
    blockerMessagesFromRules: [],
    warningMessagesFromRules: [],
    flaggedFieldsFromRules: new Map(),
    valueOverrides: {},
    dynamicRequired: new Set(),
    dynamicUnrequired: new Set(),
    hiddenSections: new Set(),
    shownSections: new Set(),
    hiddenFields: new Set(),
    shownFields: new Set(),
    jumpTarget: null,
    captchaRequired: false,
    scoreOverrides: {},
  };

  for (const match of matches) {
    const { action, action_config, rule } = match;

    // Convert to RuleResult for compatibility with rulesEngine
    const ruleResult: RuleResult = {
      id: rule.id,
      label: rule.rule_name,
      action: action as RuleResult['action'],
      message: action_config.message,
      triggered: true,
      severity: action_config.severity,
    };
    result.firedRules.push(ruleResult);

    switch (action) {
      case 'block_submission':
        result.hasBlockers = true;
        if (action_config.message) {
          result.blockerMessages.push(action_config.message);
        }
        break;

      case 'require_approval':
        result.requiresApproval = true;
        result.requiredApprovalLevel = Math.max(
          result.requiredApprovalLevel,
          action_config.approval_level || 1
        );
        break;

      case 'flag_for_review':
        // Flag the expense for review
        const flagMessage = action_config.message || 'Flagged for policy review';
        result.flaggedFields.set('expense', {
          message: flagMessage,
          severity: action_config.severity || 'warning',
        });
        if (action_config.message) {
          result.warningMessages.push(action_config.message);
        }
        break;

      case 'auto_categorize':
        if (action_config.category_id !== undefined || action_config.category_name !== undefined) {
          result.autoCategorize = {
            category_id: action_config.category_id ?? undefined,
            category_name: action_config.category_name ?? undefined,
          };
        }
        break;

      case 'warn_user':
        if (action_config.message) {
          result.warningMessages.push(action_config.message);
        }
        break;

      case 'auto_approve':
        result.autoApproval = {
          ruleId: rule.id,
          ruleName: rule.rule_name,
          reason: action_config.message || `Auto-approved by rule: ${rule.rule_name}`,
          maxAmount: action_config.max_amount,
          aiConfidenceThreshold: action_config.ai_confidence_threshold,
        };
        break;

      case 'auto_assign_supplier':
        // Auto-assign supplier is handled by the intake pipeline, not by the
        // policy evaluation result. The action_config carries contact_id and
        // optional account_code that the intake processor applies directly.
        // We still record it in firedRules so it shows in simulation output.
        break;

      case 'require_vendor_registration':
        if (action_config.severity === 'error') {
          result.hasBlockers = true;
          if (action_config.message) {
            result.blockerMessages.push(action_config.message);
          }
        } else {
          if (action_config.message) {
            result.warningMessages.push(action_config.message);
          }
        }
        // Track vendor registration requirement
        const vendorName = String(match.matched_condition_values?.find(c => c.field === 'vendor')?.value || '');
        if (vendorName) {
          result.requireVendorRegistration = {
            vendorName,
            autoCreate: action_config.auto_create_vendor === true,
          };
        }
        break;
    }
  }

  // Sync with rulesEngine result structure
  result.hasBlockersFromRules = result.hasBlockers;
  result.blockerMessagesFromRules = result.blockerMessages;
  result.warningMessagesFromRules = result.warningMessages;
  result.flaggedFieldsFromRules = result.flaggedFields;

  return result;
}

// ============================================================================
// DATABASE OPERATIONS (work_item_settings)
// NOTE: These should be moved to a server-only file or API route
// to avoid client-side build issues with database imports.
// For now, this file contains only pure business logic.
// ============================================================================
